import React from 'react';
import { Link } from 'react-router-dom';
import {
  Smartphone,
  Users,
  Heart,
  Leaf,
  Moon,
  Check,
  Star,
  ArrowRight,
  ShieldCheck,
  CreditCard,
  CalendarCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const features = [
  {
    icon: Smartphone,
    title: 'Phone-first',
    body: "Modern phones shoot stunning aurora. We'll show you exactly how to get the best from yours.",
  },
  {
    icon: Users,
    title: 'Maximum 4 guests',
    body: 'Tiny group, personal attention. This is not a coach tour.',
  },
  {
    icon: Heart,
    title: 'Built around you',
    body: 'Your goals shape the trip. We chat before you arrive so nothing is left to chance.',
  },
  {
    icon: Leaf,
    title: 'Relaxed pace',
    body: 'Excursions, not schedules. No rush, no pressure, no expertise required.',
  },
];

const itinerary = [
  {
    date: 'Sat 20 Sep',
    title: 'Arrival & welcome evening',
    body: "Settle in, meet your fellow guests over dinner. We talk through everyone's hopes for the trip so the next five days are shaped around you.",
  },
  {
    date: 'Sun 21 Sep',
    title: 'Explore Tromsø & phone photography walk',
    body: "A relaxed day exploring the city — Arctic Cathedral, harbour, local cafés. We'll cover phone tips as we go, no classroom required.",
  },
  {
    date: 'Mon 22 Sep',
    title: 'Equinox eve — prime aurora conditions',
    body: 'The September equinox traditionally brings the most spectacular displays. We position ourselves away from light pollution for the best possible view.',
  },
  {
    date: 'Tue 23 Sep',
    title: 'Fjord excursion',
    body: 'A daytime adventure into the landscape — chosen based on what the group most wants to experience. Possibilities include boat trips, mountain cable car, or coastal walks.',
  },
  {
    date: 'Wed 24 Sep',
    title: 'Free day + evening aurora watch',
    body: 'Time to explore at your own pace, rest, or shop for gifts. Evening: another aurora outing, forecast-guided to the best conditions.',
  },
  {
    date: 'Thu 25 Sep',
    title: 'Farewell morning & departures',
    body: "A final breakfast together before heading home with memories — and images — you'll keep forever.",
  },
];

const steps = [
  {
    n: '1',
    title: 'Book your spot',
    body: "you'll receive a short goals questionnaire",
  },
  {
    n: '2',
    title: 'We chat before you travel',
    body: 'your answers shape the excursions, the pace, and the focus',
  },
  {
    n: '3',
    title: "Arrive knowing it's built for you",
    body: 'not a generic itinerary, your itinerary',
  },
];

const testimonials = [
  {
    text: 'I only had my iPhone. The images I came home with are genuinely breathtaking.',
    name: 'Sarah M.',
    detail: 'Not a photographer at all',
  },
  {
    text: 'The pre-trip chat made such a difference. It felt like the trip was designed just for me.',
    name: 'James R.',
    detail: 'Solo traveller',
  },
  {
    text: "I was nervous I'd be out of my depth. I never once felt that way. Magical trip.",
    name: 'Priya K.',
    detail: 'First time in the Arctic',
  },
];

const included = [
  '5 nights accommodation in Tromsø',
  'All guided excursions & aurora outings',
  'Welcome dinner & daily breakfast',
  'Airport transfers',
  'Pre-trip personal goals consultation',
  'Phone photography guidance throughout',
  'Flexible cancellation up to 60 days',
];

const faqs = [
  {
    q: 'Do I need a proper camera?',
    a: "Not at all. Modern smartphones — especially iPhone 13 and newer, or equivalent Android — shoot brilliant aurora images. We'll show you exactly which settings to use.",
  },
  {
    q: "What if the Northern Lights don't appear?",
    a: 'September around the equinox is one of the most reliable periods of the year for aurora activity. With five nights, we have multiple chances — and we monitor forecasts daily to chase the best conditions.',
  },
  {
    q: 'Is it very cold in September?',
    a: "September is mild by Arctic standards — typically 2–8°C. You'll need warm layers but nothing extreme. We'll send a full packing list after you book.",
  },
  {
    q: 'Is it physically demanding?',
    a: "Not at all. The pace is relaxed and excursions are chosen with the group in mind. You don't need to be fit or adventurous — just curious.",
  },
  {
    q: 'Will I be with strangers?',
    a: 'Yes — but a maximum of four guests means it quickly feels like travelling with friends. Many past guests have stayed in touch long after the trip.',
  },
];

const trustBadges = [
  { icon: ShieldCheck, label: 'Secure booking via Bookeo' },
  { icon: CreditCard, label: 'Payment plans available' },
  { icon: CalendarCheck, label: 'Free cancellation 60 days out' },
];

const NorthernLightsTromso = () => {
  return (
    <div className="min-h-screen" data-testid="tromso-page">
      {/* 1. Hero */}
      <section className="relative min-h-[88vh] flex items-center justify-center overflow-hidden bg-brand-navy">
        <img
          src="https://customer-assets.emergentagent.com/job_e7d1381f-c6bc-43b9-a64c-5f521d4d18e9/artifacts/b66psys3_DSC00535%20%282%29%20-%20Edited.jpg"
          alt="Northern Lights over Arctic Norway"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/70 via-brand-navy/50 to-brand-navy/90" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto pt-24 pb-16">
          <p className="text-sm md:text-base uppercase tracking-[0.2em] text-brand-mint mb-6">
            Tromsø, Norway&nbsp;&nbsp;•&nbsp;&nbsp;20–25 September&nbsp;&nbsp;•&nbsp;&nbsp;4 spots only
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6 leading-tight">
            See the Northern Lights — and capture them on your phone
          </h1>
          <p className="text-lg md:text-xl text-white/90 font-light mb-10 leading-relaxed max-w-2xl mx-auto">
            A relaxed, personalised Arctic adventure. No camera experience needed. Just bring your smartphone and a sense of wonder.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <a
              href="https://www-2561o.bookeo.com/bookeo/b_lightchasingadventures_start.html?ctlsrc2=es3Ov6azGne2x%2BDB2b6nmeVEJUQHTqZiuRyeqdW6GfQ%3D&src=02b"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-reserve-btn"
            >
              <Button className="bg-brand-teal hover:bg-brand-teal-dark text-white px-8 py-6 text-base">
                Reserve your spot
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </a>
            <a href="#pricing" data-testid="hero-see-included-btn">
              <Button
                variant="outline"
                className="border-2 border-white/60 text-white hover:bg-white hover:text-brand-navy px-8 py-6 text-base bg-transparent"
              >
                See what's included
              </Button>
            </a>
          </div>
          <p className="text-sm text-brand-mint tracking-wide">
            Only 4 spots remaining for September
          </p>
        </div>
      </section>

      {/* 2. Who this is for */}
      <section className="py-24 bg-white" data-testid="who-this-is-for-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-brand-teal text-white text-xs font-medium px-4 py-1.5 rounded-full mb-6">
            Designed for smartphone photographers
          </span>
          <p className="text-sm uppercase tracking-[0.2em] text-brand-teal font-medium mb-3">
            Who this is for
          </p>
          <h2 className="text-3xl md:text-4xl font-light text-brand-navy mb-6 leading-tight">
            You don't need to be a photographer
          </h2>
          <p className="text-lg text-brand-muted leading-relaxed">
            This trip is for curious, adventurous people who want an unforgettable Arctic experience — and beautiful images to remember it by. Your smartphone is all you need.
          </p>
        </div>
      </section>

      {/* 3. Four feature cards */}
      <section className="py-20 bg-brand-light" data-testid="features-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => (
              <div
                key={f.title}
                className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-lg transition-shadow duration-300"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-mint/30 mb-5">
                  <f.icon className="text-brand-teal" size={24} />
                </div>
                <h3 className="text-lg font-medium text-brand-navy mb-2">{f.title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Itinerary */}
      <section className="py-24 bg-white" data-testid="itinerary-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm uppercase tracking-[0.2em] text-brand-teal font-medium mb-3">
              The experience
            </p>
            <h2 className="text-3xl md:text-4xl font-light text-brand-navy mb-5">
              Five nights in Arctic Norway
            </h2>
            <p className="text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
              September is the sweet spot — mild enough to explore comfortably, dark enough for the aurora, and timed perfectly around the autumn equinox.
            </p>
          </div>

          <div className="divide-y divide-brand-mint/30 border-t border-brand-mint/30">
            {itinerary.map((day) => (
              <div
                key={day.date}
                className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-2 md:gap-8 py-7"
              >
                <div className="text-brand-teal font-medium text-sm md:text-base whitespace-nowrap">
                  {day.date}
                </div>
                <div>
                  <h3 className="text-xl font-normal text-brand-navy mb-2">{day.title}</h3>
                  <p className="text-brand-muted leading-relaxed">{day.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Equinox callout */}
      <section className="py-24 bg-brand-navy text-white" data-testid="equinox-callout">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-purple/30 mb-6">
            <Moon className="text-brand-mint" size={30} />
          </div>
          <h2 className="text-3xl md:text-4xl font-light mb-6">
            Why September? The equinox effect.
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed">
            The September equinox is one of the most reliable times of year for aurora activity. The angle of Earth's magnetic field creates conditions that make displays more frequent and more vivid — without the extreme cold and heavy snowfall of midwinter.
          </p>
        </div>
      </section>

      {/* 6. Personalised trip */}
      <section className="py-24 bg-white" data-testid="personalised-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] text-brand-teal font-medium mb-3">
              Your trip, personalised
            </p>
            <h2 className="text-3xl md:text-4xl font-light text-brand-navy mb-5">
              We plan around you before you arrive
            </h2>
            <p className="text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
              Every guest is different. Some want to push their phone photography as far as it will go. Others just want to stand under the aurora and soak it in. Most want something in between. Before your trip, we have a conversation so nothing is left to guesswork.
            </p>
          </div>

          <div className="bg-brand-light rounded-2xl p-8 md:p-12 border border-brand-mint/30">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {steps.map((s) => (
                <div key={s.n} className="text-center md:text-left">
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-brand-teal text-white text-lg font-medium mb-4">
                    {s.n}
                  </div>
                  <h3 className="text-lg font-medium text-brand-navy mb-2">{s.title}</h3>
                  <p className="text-brand-muted leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 7. Testimonials */}
      <section className="py-24 bg-brand-light" data-testid="tromso-testimonials">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm uppercase tracking-[0.2em] text-brand-teal font-medium mb-3">
              Past guests
            </p>
            <h2 className="text-3xl md:text-4xl font-light text-brand-navy">
              What people say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="flex items-center gap-1 mb-4 text-brand-teal">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <p className="text-brand-text leading-relaxed mb-6 italic">"{t.text}"</p>
                <div className="border-t border-brand-mint/40 pt-4">
                  <p className="font-medium text-brand-navy">{t.name}</p>
                  <p className="text-sm text-brand-muted mt-0.5">{t.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Pricing */}
      <section id="pricing" className="py-24 bg-white scroll-mt-20" data-testid="pricing-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.2em] text-brand-teal font-medium mb-3">
              Your investment
            </p>
          </div>

          <div className="bg-white rounded-2xl border-2 border-brand-teal shadow-lg p-8 md:p-12">
            <div className="text-center mb-8">
              <span className="text-5xl md:text-6xl font-light text-brand-navy">£2,000</span>
              <p className="text-sm text-brand-muted mt-2">per guest · all-inclusive</p>
            </div>

            <ul className="space-y-3 mb-10">
              {included.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check className="text-brand-teal mt-0.5 flex-shrink-0" size={20} />
                  <span className="text-brand-text">{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col gap-3 mb-8">
              <a
                href="https://www-2561o.bookeo.com/bookeo/b_lightchasingadventures_start.html?ctlsrc2=es3Ov6azGne2x%2BDB2b6nmeVEJUQHTqZiuRyeqdW6GfQ%3D&src=02b"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
                data-testid="pricing-book-btn"
              >
                <Button className="w-full bg-brand-teal hover:bg-brand-teal-dark text-white py-6 text-base">
                  Book your spot
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </a>
              <Link to="/contact" className="w-full" data-testid="pricing-question-btn">
                <Button
                  variant="outline"
                  className="w-full border-2 border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-white py-6 text-base bg-transparent"
                >
                  I have a question first
                </Button>
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-6 border-t border-brand-mint/30">
              {trustBadges.map((b) => (
                <span key={b.label} className="flex items-center gap-1.5 text-xs text-brand-muted">
                  <b.icon size={15} className="text-brand-teal" />
                  {b.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQs */}
      <section className="py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] text-brand-teal font-medium mb-3">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-light text-brand-navy">
              Things people always ask
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-brand-mint/30"
              >
                <AccordionTrigger className="text-base md:text-lg text-brand-navy py-5 hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-brand-muted leading-relaxed text-base">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default NorthernLightsTromso;
